/**
 * Serviço de Acesso a eBooks - Gerenciar compras e acesso
 * Armazena e gerencia quais eBooks o usuário tem acesso
 */

class EbookAccessService {
  /**
   * Chave de armazenamento no localStorage
   */
  STORAGE_KEY = 'purchasedEbooks';
  
  /**
   * Obtém lista de eBooks comprados do localStorage
   * @returns {Array} Array de objetos com dados dos eBooks comprados
   */
  getPurchasedEbooks() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('❌ [EbookAccessService] Erro ao ler eBooks comprados:', error);
      return [];
    }
  }

  /**
   * Obtém IDs dos eBooks comprados
   * @returns {Array} Array de IDs dos eBooks
   */
  getPurchasedEbookIds() {
    return this.getPurchasedEbooks().map(ebook => ebook.ebookId);
  }

  /**
   * Verifica se um eBook foi comprado
   * @param {string} ebookId - ID do eBook
   * @returns {boolean}
   */
  hasAccessToEbook(ebookId) {
    return this.getPurchasedEbookIds().includes(ebookId);
  }

  /**
   * Adiciona eBooks à lista de comprados
   * @param {Array} ebooks - Array de eBooks a adicionar
   * @param {Object} paymentInfo - Informações do pagamento (método, txid/chargeId, data, email, cpf, name)
   */
  grantAccess(ebooks, paymentInfo = {}) {
    try {
      if (!Array.isArray(ebooks)) {
        console.error('❌ [EbookAccessService] ebooks deve ser um array');
        return false;
      }

      console.log('📝 [EbookAccessService] Tentando adicionar', ebooks.length, 'eBook(s) ao localStorage');
      console.log('   Detalhes:', ebooks);
      console.log('   Dados de contato:', { email: paymentInfo.email, cpf: paymentInfo.cpf });

      const purchased = this.getPurchasedEbooks();
      console.log('   Compras existentes:', purchased.length);

      const now = new Date().toISOString();

      // Adicionar cada eBook à lista de comprados
      let addedCount = 0;
      ebooks.forEach((ebook) => {
        // Verificar se já foi comprado
        if (!purchased.find(p => p.ebookId === ebook.ebookId)) {
          purchased.push({
            ebookId: ebook.ebookId || ebook.id,
            titulo: ebook.titulo || ebook.title || 'eBook',
            preco: ebook.preco || ebook.price || 0,
            purchasedAt: now,
            paymentMethod: paymentInfo.method || 'unknown',
            transactionId: paymentInfo.txid || paymentInfo.chargeId || 'unknown',
            expiresAt: null, // null = acesso permanente
            // Dados de contato para sincronização entre navegadores
            email: paymentInfo.email || '',
            cpf: paymentInfo.cpf || '',
            name: paymentInfo.name || '',
          });
          addedCount++;
          console.log(`   ✅ Adicionado: ${ebook.ebookId} (${ebook.titulo || ebook.title})`);
        } else {
          console.log(`   ℹ️ Já existe: ${ebook.ebookId}`);
        }
      });

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(purchased));
      console.log(`✅ [EbookAccessService] localStorage atualizado com ${addedCount} novo(s) eBook(s)`);
      console.log('   Total no localStorage:', purchased.length);

      return true;
    } catch (error) {
      console.error('❌ [EbookAccessService] Erro ao liberar acesso:', error);
      return false;
    }
  }

  /**
   * Remove acesso a um eBook
   * @param {string} ebookId - ID do eBook
   */
  revokeAccess(ebookId) {
    try {
      let purchased = this.getPurchasedEbooks();
      purchased = purchased.filter(ebook => ebook.ebookId !== ebookId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(purchased));
      console.log('✅ [EbookAccessService] Acesso revogado para eBook:', ebookId);
      return true;
    } catch (error) {
      console.error('❌ [EbookAccessService] Erro ao revogar acesso:', error);
      return false;
    }
  }

  /**
   * Limpa todos os eBooks comprados (para teste/debug)
   */
  clearAllAccess() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('✅ [EbookAccessService] Todos os acessos foram removidos');
      return true;
    } catch (error) {
      console.error('❌ [EbookAccessService] Erro ao limpar acessos:', error);
      return false;
    }
  }

  /**
   * Obtém informações detalhadas sobre um eBook comprado
   * @param {string} ebookId - ID do eBook
   * @returns {Object|null}
   */
  getEbookInfo(ebookId) {
    const purchased = this.getPurchasedEbooks();
    return purchased.find(ebook => ebook.ebookId === ebookId) || null;
  }

  /**
   * Obtém estatísticas de acesso
   * @returns {Object}
   */
  getAccessStats() {
    const purchased = this.getPurchasedEbooks();
    const total = purchased.length;
    const totalSpent = purchased.reduce((sum, ebook) => sum + (ebook.preco || 0), 0);

    return {
      totalEbooksAccess: total,
      totalSpent,
      ebooks: purchased,
    };
  }
}

export const ebookAccessService = new EbookAccessService();
